// Declared once in `shared/organizations/userProfile.ts`, beside the `UserProfileRow` the service is
// annotated with. This was a hand-maintained copy of a projection nothing checked it against.
import type { UserProfile } from '#shared/organizations/userProfile'
import type { SecondPerson } from '#shared/organizations/secondPerson'
import type { OnboardingAction } from '#shared/onboarding/actions'
import type { LawyerCarnetInput } from '#shared/onboarding/credential'
import { getStoredOrgOverride } from '~/utils/orgOverride'
import { requestFailureKind, type RequestFailureKind } from '~/utils/requestFailure'

/**
 * Why the profile is absent, not just that it is: not fetched yet, or the service could not answer.
 * The onboarding gate fails closed on `unavailable` (`gateFor` in shared/onboarding/flow.ts).
 */
export type ProfileStatus = 'idle' | 'ok' | 'unavailable'

/**
 * Centralized, SSR-aware user profile store.
 *
 * Call `ensure()` once in a layout or page that runs on SSR —
 * it fetches via cookie-forwarded request and hydrates into useState,
 * so all components (sidebar, welcome, settings) share one source of truth
 * with no client-side flash.
 */
export function useUserProfile() {
  const { apiFetch } = useApi()
  const profile = useState<UserProfile | null>('user-profile', () => null)
  const loading = useState<boolean>('user-profile-loading', () => false)
  const status = useState<ProfileStatus>('user-profile-status', () => 'idle')
  /**
   * WHY the last load failed, as a kind (`requestFailure.ts`) — never the error itself, which would not
   * survive the SSR payload. Lets a screen tell "no connection" from a server error (MAR-933 F8);
   * null after a load that succeeded.
   */
  const failure = useState<RequestFailureKind | null>('user-profile-failure', () => null)

  /**
   * The single fetch. `ensure()` and `fetchProfile()` are entry points, not
   * separate implementations — they used to be copies of each other, which is a
   * standing invitation for the two to drift.
   */
  async function load() {
    loading.value = true
    try {
      profile.value = await apiFetch<UserProfile>('/api/profile')
      status.value = 'ok'
      failure.value = null
    } catch (err) {
      profile.value = null
      failure.value = requestFailureKind(err)
      status.value = 'unavailable'
    } finally {
      loading.value = false
    }
  }

  /**
   * The load `ensure()` already has running. A second caller awaits it instead of
   * starting its own — `profile.value` is still null while the first request is in
   * flight, so the `if (profile.value)` guard alone lets both through and fires
   * `/api/profile` twice. Both real entry points run on the same client tick
   * (`middleware/auth.global.ts` → `useOnboarding().nextStep()`, and
   * `pages/research.vue`'s `onMounted`), so the race is the normal case, not a corner.
   *
   * It hangs off `useNuxtApp()`, which is per-request — a module-level variable would
   * be shared across SSR requests, i.e. one user's in-flight profile handed to another.
   * Cleared when the load settles, including on failure, so a transient error can be
   * retried rather than pinned for the life of the page.
   */
  const nuxtApp = useNuxtApp() as unknown as { _userProfileInflight?: Promise<void> }

  /**
   * SSR-safe fetch — call from layout so profile is ready before render.
   *
   * A loaded profile is reused only while it was fetched under the org override this browser holds
   * NOW. The SSR document request carries cookies, never `x-marcus-org-override` (MAR-475), so after a
   * full page load the hydrated profile is the superadmin's OWN one even while they act as another
   * org. Returning early on it handed the route middleware's onboarding gate the wrong profile, and
   * a superadmin whose own row had not entered onboarding was sent to `/app/setup` (MAR-933 F2). The
   * re-read happens once: after it, the loaded override matches the stored one. An override the
   * server refuses is cleared by `app/plugins/api.ts`, so a refusal cannot loop either. On the server
   * `getStoredOrgOverride()` is always null, which matches the SSR profile — nothing re-reads there.
   */
  async function ensure() {
    if (profile.value && (profile.value.org_override_id ?? null) === getStoredOrgOverride()) return
    if (nuxtApp._userProfileInflight) return nuxtApp._userProfileInflight

    const inflight = load().finally(() => {
      nuxtApp._userProfileInflight = undefined
    })

    nuxtApp._userProfileInflight = inflight
    return inflight
  }

  /** Re-read the profile after something that changes it (joining an org, saving a name). */
  async function fetchProfile() {
    await load()
  }

  /**
   * Partial by design: a key left out is left alone server-side, so the settings page can save a
   * second-person form without also rewriting the name and vice versa. Do not "helpfully" fill in
   * the missing key from `profile.value` — that turns every save into a full overwrite and races
   * with anything else editing the profile.
   */
  async function updateProfile(data: {
    display_name?: string
    second_person?: SecondPerson
    dismiss_tips?: true
    /** The name screen's carnet de abogado (MAR-1146) — see `shared/onboarding/credential.ts`. */
    credential?: LawyerCarnetInput
    /** One onboarding action (MAR-933) — see `shared/onboarding/actions.ts`. */
    onboarding?: OnboardingAction
  }) {
    profile.value = await apiFetch<UserProfile>('/api/profile', {
      method: 'PUT',
      body: data,
    })
    // A successful write is also proof the profile is reachable — leaving the
    // status behind here would let a stale 'idle'/'unavailable' outlive it.
    status.value = 'ok'
  }

  const displayName = computed(() => profile.value?.display_name || profile.value?.email || '')
  const initials = computed(() => {
    const name = profile.value?.display_name
    if (name) {
      const parts = name.trim().split(/\s+/)
      return parts.length >= 2
        ? (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
        : name[0]!.toUpperCase()
    }
    return profile.value?.email?.[0]?.toUpperCase() || '?'
  })

  const organizationName = computed(() => profile.value?.organization_name || '')
  const organizationMainJurisdiction = computed(() => profile.value?.organization_main_jurisdiction || '')

  return {
    profile,
    status,
    failure,
    loading,
    ensure,
    fetchProfile,
    updateProfile,
    displayName,
    initials,
    organizationName,
    organizationMainJurisdiction,
  }
}
